import { makeAutoObservable } from 'mobx';
import nanoid from 'ati-utils/nanoid';
import {
  getSkipQsValue,
  getTakeQsValue,
  setSkipQsValue,
  setTakeQsValue,
  getGroupIdFromQsMethods,
  getAtiDocsFromQsMethods,
  getAtiOrdersFromQsMethods,
  getAutoparkFromQsMethods,
  getVerifiedFromQsMethods,
  getConfirmedContactsFromQsMethods,
  getEpdMedalFromQsMethods,
} from '../utils/uiQueryString';

import {
  getSkipValidated,
  getTakeValidated,
} from '../utils/uiParamsValidation';

import {
  groupIdParamName,
  groupIds,
  groupIdsToFirmTypes,
  paramsDefaults,
} from '../constants/uiParams';
import { isDesktop } from '../utils/screenSizes';
import strings from '../strings';
import { getUrlWithNewQsValue } from '../utils/queryString';

const getTabInfo = newGroupId => ({
  id: newGroupId,
  href: getUrlWithNewQsValue(groupIdParamName, newGroupId),
});

class UiStore {
  pageSize = null;
  pageNumber = null;
  skip = paramsDefaults.Skip;
  take = paramsDefaults.Take;
  reverse = paramsDefaults.Reverse;
  verified = paramsDefaults.Verified;
  autopark = paramsDefaults.Autopark;
  atiOrders = paramsDefaults.AtiOrders;
  atiDocs = paramsDefaults.AtiDocs;
  groupId = paramsDefaults.GroupId;
  firmTypes = paramsDefaults.FirmTypes;
  confirmedContacts = paramsDefaults.confirmedContacts;
  epdMedal = paramsDefaults.epdMedal;
  notificationsList = [];
  isMobile = !isDesktop();
  showAllFilters = isDesktop();
  tabs = [
    {
      label: strings.carrierTab,
      ...getTabInfo(groupIds.Carrier),
    },
    {
      label: strings.dispatcherTab,
      ...getTabInfo(groupIds.Dispatcher),
    },
    {
      label: strings.shipperTab,
      ...getTabInfo(groupIds.Shipper),
    },
  ];

  constructor() {
    makeAutoObservable(this);
    this.validateAndSet();
  }

  // Getters
  get groupFirmTypes() {
    return groupIdsToFirmTypes[this.groupId];
  }

  get uiContext() {
    const firmTypes = this.firmTypes.length > 0
      ? this.firmTypes
      : groupIdsToFirmTypes[this.groupId];

    return {
      skip: this.skip,
      take: this.take,
      atiOrders: this.atiOrders,
      autopark: this.autopark,
      atiDocs: this.atiDocs,
      reverse: this.reverse,
      verified: this.verified,
      confirmedContacts: this.confirmedContacts,
      epdMedal: this.epdMedal,
      firmTypes,
    };
  }

  get dropdownWidth() {
    const mobileWidth = 280;
    const desktopWidth = 232;
    const value = this.isMobile ? mobileWidth : desktopWidth;

    return { min: value, max: value };
  }

  // Setters
  setPageSettings = () => {
    this.pageSize = this.take;
    this.pageNumber = (this.skip / this.take) + 1;
  };

  validateAndSet = () => {
    const skip = getSkipQsValue();
    const take = getTakeQsValue();

    this.setSkipTakeValues(skip, take);

    this.setGroupIdFromQs();
    this.setAtiDocsFromQs();
    this.setAutoparkFromQs();
    this.setAtiOrdersFromQs();
    this.setVerifiedFromQs();
    this.setConfirmedContactsFromQs();
    this.setEpdMedalFromQs();
  };

  setSkipTakeValues = (skip, take) => {
    // порядок важен
    this.setTakeValidated(take);
    this.setSkipValidated(skip);
    this.setPageSettings();
  };

  setTakeValidated = (take) => {
    const validated = getTakeValidated(take);

    this.take = validated;
    setTakeQsValue(validated);
  };

  setSkipValidated = (skip) => {
    const validated = getSkipValidated(skip, this.take);

    this.skip = validated;
    setSkipQsValue(validated);
  };

  setGroupIdFromQs = () => {
    const {
      value,
      clear,
    } = getGroupIdFromQsMethods();

    const groupId = value || paramsDefaults.GroupId;

    clear();
    this.switchGroup(groupId);
  };

  setAtiDocsFromQs = () => {
    const {
      value,
      clear,
    } = getAtiDocsFromQsMethods();

    const atiDocs = value === 1;

    clear();
    this.setAtiDocs(atiDocs);
  }

  setAutoparkFromQs = () => {
    const {
      value,
      clear,
    } = getAutoparkFromQsMethods();

    const autopark = value === 1;

    clear();
    this.setAutopark(autopark);
  }

  setAtiOrdersFromQs = () => {
    const {
      value,
      clear,
    } = getAtiOrdersFromQsMethods();

    const atiOrders = value === 1;

    clear();
    this.setAtiOrders(atiOrders);
  }

  setVerifiedFromQs = () => {
    const {
      value,
      clear,
    } = getVerifiedFromQsMethods();

    const verified = value === 1;

    clear();
    this.setVerified(verified);
  }

  setConfirmedContactsFromQs = () => {
    const {
      value,
      clear,
    } = getConfirmedContactsFromQsMethods();

    const confirmedContacts = value === 1;

    clear();
    this.setConfirmedContacts(confirmedContacts);
  }

  setEpdMedalFromQs = () => {
    const {
      value,
      clear,
    } = getEpdMedalFromQsMethods();

    const epdMedal = value === 1;

    clear();
    this.setEpdMedal(epdMedal);
  }

  setPageNumber = (number) => {
    const skip = (number - 1) * this.take;

    this.setSkipValidated(skip);
    this.pageNumber = number;
  };

  switchGroup = (groupId) => {
    const isValid = Object
      .values(groupIds)
      .includes(groupId);

    const validatedGroupId = isValid ? groupId : paramsDefaults.GroupId;

    this.setGroupId(validatedGroupId);
    this.setFirmTypes(groupIdsToFirmTypes[validatedGroupId]);
  };

  setGroupId = (groupId) => {
    this.groupId = groupId;
  };

  setFirmTypes = (firmTypes) => {
    this.firmTypes = firmTypes;
  };

  setPageSize = (pageSize) => {
    this.setSkipTakeValues(paramsDefaults.Skip, pageSize);
  };

  setReverse = (reverse) => {
    this.reverse = reverse;
  };

  setVerified = (verified) => {
    this.verified = verified;
  };

  setAutopark = (autopark) => {
    this.autopark = autopark;
  };

  setAtiOrders = (atiOrders) => {
    this.atiOrders = atiOrders;
  };

  setAtiDocs = (atiDocs) => {
    this.atiDocs = atiDocs;
  };

  setConfirmedContacts = (confirmedContacts) => {
    this.confirmedContacts = confirmedContacts;
  };

  setEpdMedal = (epdMedal) => {
    this.epdMedal = epdMedal;
  }

  setNotificationsList = (notificationsList) => {
    this.notificationsList = notificationsList;
  };

  addNotification = ({ content, type, delay }) => {
    this.setNotificationsList([{
      id: nanoid(),
      content,
      type,
      delay,
    }]);
  };

  clearNotificationsList = () => {
    this.setNotificationsList([]);
  };

  resetSkipTake = () => {
    this.setSkipTakeValues(paramsDefaults.Skip, this.take);
  };

  resetFilterToDefaults = () => {
    this.atiOrders = paramsDefaults.AtiOrders;
    this.autopark = paramsDefaults.Autopark;
    this.atiDocs = paramsDefaults.AtiDocs;
    this.reverse = paramsDefaults.Reverse;
    this.verified = paramsDefaults.Verified;
    this.groupId = paramsDefaults.GroupId;
    this.firmTypes = paramsDefaults.FirmTypes;
    this.confirmedContacts = paramsDefaults.confirmedContacts;
    this.epdMedal = paramsDefaults.epdMedal;
  };

  setIsMobile = (isMobile) => {
    this.isMobile = isMobile;
  }

  setShowAllFilters = (showAllFilters) => {
    this.showAllFilters = showAllFilters;
  }
}

export default UiStore;
